<template>
    <ui-crud title="RAMS" subtitle="List of Risk Assessment Method Statements for Package" :fetch="fetch" ref="crud"
             :pipe-with="pipeItems" groupBy="status" :create="createNew">

        <template v-slot:new-button>
            <div class="flex items-center">
                <h1 v-text="'RAMS'" class="text-lg font-medium text-gray-900"></h1>
                <div v-if="!ramsClipboard || (isNull(ramsClipboard[0]))"
                     class="ml-auto hidden sm:inline-flex">
                    <ui-button-new small class="ml-auto hidden sm:inline-flex" icon="fas fa-plus"
                                   @click.prevent="createNew()">New
                    </ui-button-new>
                </div>
                <div v-else class="ml-auto hidden sm:inline-flex">
                    <ui-dropdown placement="bottom-end" class="ml-auto" menu-class="p-3 w-64 z-[60]" :key="userKey">
                        <template #default="{ toggle }">
                            <ui-button-new @click="toggle" small class="ml-auto hidden sm:inline-flex">
                                <i class="text-gray-400 flex-shrink-0 mr-2 fa fa-chevron-down"></i>
                                New
                            </ui-button-new>
                        </template>
                        <template #menu="{ close }">
                            <ui-dropdown-item icon="fas fa-print" @click="createNew();close();">Create new RAMS
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="(ramsClipboard && ramsClipboard[0])"></ui-dropdown-divider>
                            <ui-dropdown-item icon="fas fa-paste"
                                              v-if="(ramsClipboard && ramsClipboard[0])"
                                              @click="pasteRams(ramsClipboard[0]);close();">
                                Paste as New RAMS
                            </ui-dropdown-item>
                        </template>
                    </ui-dropdown>
                </div>
            </div>
            <p v-text="'List of RAMS for this Package'" class="mt-1 text-sm text-gray-600"></p>
            <ui-button-new class="sm:hidden mt-4" icon="fas fa-plus"
                           @click.prevent="createNew()">New
            </ui-button-new>
        </template>

        <!-- Filters. -->
        <template #filters>
            <div class="relative">
                <ui-input class="mt-6" field="bg-gray-50 pr-10" name="search" v-model="search"></ui-input>
                <div class="absolute right-0 inset-y-0 h-full flex items-center justify-center p-3 text-gray-400">
                    <i class="fas fa-search"></i>
                </div>
            </div>
        </template>
        <!-- List item. -->
        <template #list-item="{ item, select, active }">
            <ui-crud-card
                :title="item.date ? `${moment(item.date).format('MMMM Do, YYYY')}` : '-- No Date --'"
                :subtitle="item.locationOfWorks"
                :metadata="item.contractReference"
                :active="active"
                @click="select()"
            ></ui-crud-card>
        </template>

        <!-- Create form. -->
        <template #create="{ addItem }">
            <rams-show :package-reference="packageReference" :package-name="packageName"
                       :readonly="(!packageReference)"
                       :contacts="contacts"
                       @duplicate="duplicate"
                       @deleted="selectFirst"
                       :add-item="addItem"></rams-show>
        </template>

        <!-- Edit form. -->
        <template #edit="{ selectedItem, setEditMode }">
            <rams-show :package-reference="selectedItem.packageReference"
                       :readonly="(!packageReference)"
                       :package-name="selectedItem.packageName"
                       :contacts="contacts"
                       @duplicate="duplicate"
                       @deleted="selectFirst"
                       :item="selectedItem"
                       :edit="setEditMode"></rams-show>
        </template>

        <!-- Show page. -->
        <template #show="{ selectedItem, setEditMode, removeItem }">
            <rams-show :package-reference="selectedItem.packageReference"
                       :package-name="selectedItem.packageName"
                       :readonly="(!packageReference)"
                       :contacts="contacts"
                       @duplicate="duplicate"
                       @deleted="selectFirst"
                       :item="selectedItem" :edit="setEditMode"
                       :removeItem="removeItem"></rams-show>
        </template>
    </ui-crud>
</template>

<script>
import {Contact, RAMS} from '@models'
import UiInput from "../../ui/UiInput.vue";
import RamsShow from "./RamsShow.vue";
import {uuid} from "../../../utils";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import remoteStorage from "../../../utils/remoteStorage";
import User from "../../../services/User";
import {cloneDeep, isNull} from "lodash";
import moment from "moment";

export default {
    components: {UiDropdownDivider, UiDropdownItem, UiDropdown, UiButtonNew, RamsShow, UiInput},
    props: {
        packageReference: null,
        packageName: null,
        showWhenLoaded: null,
        defaultSiteAddress: null,
        defaultPrincipleContractor: null,
    },
    data() {
        let ramsClipboardData = [];
        try {
            ramsClipboardData = remoteStorage.getItem('rams-clipboard') ? JSON.parse(remoteStorage.getItem('rams-clipboard')) : [];
        } catch (e) {
            //continue on JSON ERROR
        }

        return {
            search: '',
            user: {},
            userKey: 1,
            ramsClipboard: ramsClipboardData,
            contacts: {loaded: false, data: []}
        }
    },
    watch: {
        userClipboardToBeWatched: {
            handler(newValue, oldValue) {
                this.ramsClipboard = remoteStorage.getItem('rams-clipboard') ? JSON.parse(remoteStorage.getItem('rams-clipboard')) : [];
                this.userKey++;
            },
        },
    },
    computed: {
        userClipboardToBeWatched() {
            return this.user.remoteStorage ? this.user.remoteStorage['rams-clipboard']
                : '';
        },
    },
    methods: {
        moment,
        isNull,
        async fetch(itemId) {
            this.loaded = false
            this.items = await RAMS.all(this.packageReference)
            if (itemId) {
                this.$refs.crud.selectItem(this.items.find((i) => i.id === itemId))
            } else if (this.items.length) {
                this.$refs.crud.selectItem(this.items[0])
            }
            this.loaded = true
            return this.items;
        },
        selectFirst() {
            this.$nextTick(() => {
                if (this.$refs.crud.items.length) {
                    this.$refs.crud.selectItem(this.$refs.crud.items[0])
                }
            })
        },
        pipeItems(items) {
            const query = this.search.toLowerCase().split(/[^A-Za-z0-9.-]+/);
            if (query.length) {
                return items.filter((dd) => {
                    return query.every((token) => {
                        return dd.tokens.includes(token);
                    })
                });
            }
            return items;
        },
        async createNew() {
            let id = uuid();
            await RAMS.create(this.packageReference, id, {
                packageName: this.packageName,
                siteAddress: this.defaultSiteAddress,
                principleContractor: this.defaultPrincipleContractor,
                contractReference: this.suggestedContractReference(),
            });
            await this.$refs.crud.fetchItems()
            this.$nextTick(() => {
                this.$refs.crud.selectItem(this.$refs.crud.items.find((i) => i.id === id))
            })
        },
        suggestedContractReference() {
            // LOOK through items and find the highest - then add 1 to it.
            let max = 0;
            const reg = RegExp("(.*)" + this.packageReference + "([- ]*)([0-9]*)", 'g');
            this.$refs.crud.items.forEach((i) => {
                let matches = reg.exec(i.contractReference);
                if (matches && matches[3]) {
                    max = Math.max(max, parseInt(matches[3]));
                }
            })
            return 'RAMS ' + this.packageReference + '-' + (max + 1)
        },
        async pasteRams(o) {
            let id = uuid();
            o.packageName = this.packageName;
            o.id = id;
            o.attendees = [];
            o.date = moment().format('YYYY-MM-DD');
            o.principleContractor = this.defaultPrincipleContractor;
            o.locationOfWorks = '';
            o.siteAddress = this.defaultSiteAddress;
            o.contractManager = '';
            o.contractReference = this.suggestedContractReference();
            await RAMS.create(this.packageReference, id, o);
            await this.$refs.crud.fetchItems()
            this.$nextTick(() => {
                this.$refs.crud.selectItem(this.$refs.crud.items.find((i) => i.id === id))
            })
        },
        async duplicate(o) {
            o = cloneDeep(o)
            let id = uuid();
            o.packageName = this.packageName;
            o.id = id;
            o.attendees = [];
            o.contractReference = this.suggestedContractReference();
            o.date = moment().format('YYYY-MM-DD');
            await RAMS.create(this.packageReference, id, o);
            await this.$refs.crud.fetchItems()
            this.$nextTick(() => {
                this.$refs.crud.selectItem(this.$refs.crud.items.find((i) => i.id === id))
            })
        }
    },
    async mounted() {
        await User.register()
        this.user = User.user
        this.contacts.data = await Contact.all()
        this.contacts.loaded = true;
        if (this.showWhenLoaded) {
            this.$refs.crud.fetch(this.showWhenLoaded)
        } else {
            this.selectFirst();
        }
    }
}
</script>
